import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Button,
  Chip,
  Slider,
  Tabs,
  Tab,
  Paper,
  Menu,
  MenuItem,
  Fade,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Replay10Icon from '@mui/icons-material/Replay10';
import Forward10Icon from '@mui/icons-material/Forward10';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import SpeedIcon from '@mui/icons-material/Speed';
import ShareIcon from '@mui/icons-material/Share';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import DownloadIcon from '@mui/icons-material/Download';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import SportsHockeyIcon from '@mui/icons-material/SportsHockey';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import CloseIcon from '@mui/icons-material/Close';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { GameVideoSummary, GoalHighlight, StoryboardMetadata, TimelineEventItem, TimelineNote, TimelineClip } from '../../types';
import { isGoalByJets, isJetsHomeGame, getGoalTeamName, getTargetTeamName } from '../../utils/gameScoreUtils';
import { StoryboardPreview } from './studio/StoryboardPreview';

export interface MobileWatchViewProps {
  storyboardMetadata?: StoryboardMetadata | null;
  videoAspectRatio?: number | null;
  authToken?: string | null;
  game: GameVideoSummary;
  highlights: GoalHighlight[];
  isJetsHome?: boolean;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  videoStreamUrl: string;
  scrubVideoRef?: React.RefObject<HTMLVideoElement | null>;
  scrubStreamUrl?: string;
  isScrubbingUI?: boolean;
  isVideoBuffering?: boolean;
  isDelayedSeeking?: boolean;
  delayedSeekTime?: number;
  selectedFeed: 'auto' | 'pano';
  hasAuto: boolean;
  hasPano: boolean;
  onFeedChange: (feed: 'auto' | 'pano') => void;
  isPlaying: boolean;
  onTogglePlay: () => void;
  currentTime: number;
  duration: number;
  onSeek: (seconds: number, isScrubbing?: boolean) => void;
  onRelativeSeek: (seconds: number) => void;
  playbackRate: number;
  onSetPlaybackRate: (rate: number) => void;
  onJumpToGoal: (timestampSeconds: number) => void;
  onJumpToPeriod: (period: number) => void;
  isReelMode: boolean;
  currentReelIndex: number;
  onPlayHighlightClip: (index: number) => void;
  onExitReelMode: () => void;
  onShareCurrentTime: () => void;
  onDownloadHighlightClip?: (highlight: GoalHighlight) => void;
  onVideoPlaybackError: (e: React.SyntheticEvent<HTMLVideoElement, Event>) => void;
  onVideoEnded: () => void;
  onTimeUpdate: () => void;
  onSeeking?: () => void;
  onSeeked: () => void;
  onWaiting?: () => void;
  onPlaying?: () => void;
  onCanPlay?: () => void;
  onLoadedMetadata: () => void;
  onToggleDesktopMode?: () => void;
  timelineEvents?: TimelineEventItem[];
  onSelectTimelineEvent?: (event: TimelineEventItem) => void;
  onDownloadClipPrompt?: (clip: TimelineClip) => void;
}

const EMPTY_TIMELINE_EVENTS: TimelineEventItem[] = [];

const formatSeconds = (sec: number): string => {
  if (!sec || isNaN(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

export const MobileWatchView: React.FC<MobileWatchViewProps> = ({
  storyboardMetadata,
  videoAspectRatio,
  authToken,
  game,
  highlights,
  videoRef,
  videoStreamUrl,
  scrubVideoRef,
  scrubStreamUrl,
  isScrubbingUI,
  isVideoBuffering,
  isDelayedSeeking,
  delayedSeekTime,
  selectedFeed,
  hasAuto,
  hasPano,
  onFeedChange,
  isPlaying,
  onTogglePlay,
  currentTime,
  duration,
  onSeek,
  onRelativeSeek,
  playbackRate,
  onSetPlaybackRate,
  onJumpToGoal,
  onJumpToPeriod,
  isReelMode,
  currentReelIndex,
  onPlayHighlightClip,
  onExitReelMode,
  onShareCurrentTime,
  onDownloadHighlightClip,
  onVideoPlaybackError,
  onVideoEnded,
  onTimeUpdate,
  onSeeking,
  onSeeked,
  onWaiting,
  onPlaying,
  onCanPlay,
  onLoadedMetadata,
  onToggleDesktopMode,
  timelineEvents = EMPTY_TIMELINE_EVENTS,
  onSelectTimelineEvent,
  onDownloadClipPrompt,
  isJetsHome: isJetsHomeProp,
}) => {
  const isJetsHome = isJetsHomeProp ?? isJetsHomeGame(game);
  const focalTeamFull = getTargetTeamName(game, false);
  const navigate = useNavigate();
  const playerContainerRef = useRef<HTMLDivElement | null>(null);

  // Controls visibility state (persists until user taps outside buttons to dismiss)
  const [showControls, setShowControls] = useState<boolean>(true);
  const touchHandledRef = useRef<boolean>(false);

  // Speed selection menu anchor
  const [speedAnchorEl, setSpeedAnchorEl] = useState<null | HTMLElement>(null);

  // Active tab: 0 = Goals, 1 = Clips
  const [activeTab, setActiveTab] = useState<number>(0);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // Landscape orientation state (auto fill-screen in landscape)
  const [isLandscape, setIsLandscape] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    if (window.screen?.orientation?.type) {
      return window.screen.orientation.type.startsWith('landscape');
    }
    if (typeof (window as any).orientation !== 'undefined') {
      return Math.abs((window as any).orientation) === 90;
    }
    if (window.matchMedia) {
      return window.matchMedia('(orientation: landscape) and (max-height: 550px)').matches;
    }
    return false;
  });

  const isFillScreen = isFullscreen || isLandscape;

  // Video viewpoint zoom & pan state (mobile pinch-to-zoom impacting only the video viewport)
  const [zoom, setZoom] = useState<number>(1.0);
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isInteracting, setIsInteracting] = useState<boolean>(false);

  // Double-tap skip feedback indicator
  const [skipFeedback, setSkipFeedback] = useState<{ direction: 'left' | 'right'; id: number } | null>(null);

  // Local smooth scrubbing state
  const [isScrubbing, setIsScrubbing] = useState<boolean>(false);
  const [localScrubTime, setLocalScrubTime] = useState<number>(0);

  // Touch & Gesture Tracking Refs
  const touchStateRef = useRef<{
    isPinching: boolean;
    startPinchDist: number;
    startZoom: number;
    startPinchMid: { x: number; y: number };
    startPan: { x: number; y: number };
    isPanning: boolean;
    touchStart: { x: number; y: number };
    startPanSingle: { x: number; y: number };
    hasPanned: boolean;
    lastTapTime: number;
    lastTapPos: { x: number; y: number };
    singleTapTimeout: NodeJS.Timeout | null;
  }>({
    isPinching: false,
    startPinchDist: 0,
    startZoom: 1.0,
    startPinchMid: { x: 0, y: 0 },
    startPan: { x: 0, y: 0 },
    isPanning: false,
    touchStart: { x: 0, y: 0 },
    startPanSingle: { x: 0, y: 0 },
    hasPanned: false,
    lastTapTime: 0,
    lastTapPos: { x: 0, y: 0 },
    singleTapTimeout: null,
  });

  // Chronologically sorted goals (P1 -> P2 -> P3 -> OT, sorted by videoTimestamp)
  const sortedGoals = useMemo(() => {
    const list = [...(game.goals || [])];
    return list.sort((a, b) => {
      if (a.videoTimestamp !== undefined && b.videoTimestamp !== undefined && a.videoTimestamp !== b.videoTimestamp) {
        return a.videoTimestamp - b.videoTimestamp;
      }
      const pA = a.period || 1;
      const pB = b.period || 1;
      if (pA !== pB) return pA - pB;
      return (a.goalNumber || 0) - (b.goalNumber || 0);
    });
  }, [game.goals]);

  // Chronologically sorted user notes and clips
  const sortedTimelineEvents = useMemo(() => {
    return [...timelineEvents].sort((a, b) => {
      const timeA = a.eventType === 'note' ? a.time : a.startTime;
      const timeB = b.eventType === 'note' ? b.time : b.startTime;
      return timeA - timeB;
    });
  }, [timelineEvents]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Listen to orientation changes to automatically enter fill-screen in landscape
  useEffect(() => {
    const handleOrientation = () => {
      let landscape = false;
      if (window.screen?.orientation?.type) {
        landscape = window.screen.orientation.type.startsWith('landscape');
      } else if (typeof (window as any).orientation !== 'undefined') {
        landscape = Math.abs((window as any).orientation) === 90;
      } else if (window.matchMedia) {
        landscape = window.matchMedia('(orientation: landscape) and (max-height: 550px)').matches;
      }
      setIsLandscape(landscape);
    };

    handleOrientation();

    if (window.screen?.orientation?.addEventListener) {
      window.screen.orientation.addEventListener('change', handleOrientation);
    }
    window.addEventListener('orientationchange', handleOrientation);
    window.addEventListener('resize', handleOrientation);

    return () => {
      if (window.screen?.orientation?.removeEventListener) {
        window.screen.orientation.removeEventListener('change', handleOrientation);
      }
      window.removeEventListener('orientationchange', handleOrientation);
      window.removeEventListener('resize', handleOrientation);
    };
  }, []);

  // In landscape mode, automatically request browser fullscreen to hide URL and navigation bars
  useEffect(() => {
    if (isLandscape) {
      const el = playerContainerRef.current;
      if (el && !document.fullscreenElement) {
        if (el.requestFullscreen) {
          el.requestFullscreen({ navigationUI: 'hide' }).catch(() => {});
        } else if ((el as any).webkitRequestFullscreen) {
          (el as any).webkitRequestFullscreen();
        }
      }
    } else {
      if (document.fullscreenElement) {
        if (document.exitFullscreen) {
          document.exitFullscreen().catch(() => {});
        } else if ((document as any).webkitExitFullscreen) {
          (document as any).webkitExitFullscreen();
        }
      }
    }
  }, [isLandscape]);

  // Prevent background page scrolling when in fill-screen / landscape mode
  useEffect(() => {
    if (isFillScreen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    }
  }, [isFillScreen]);

  // Auto-hide controls after 3.5s of playback
  useEffect(() => {
    if (!showControls || !isPlaying) return;
    const timer = setTimeout(() => {
      setShowControls(false);
    }, 3500);
    return () => clearTimeout(timer);
  }, [showControls, isPlaying]);

  // Track player container dimensions for 1:1 mobile video aspect-ratio alignment
  const [mobileContainerDimensions, setMobileContainerDimensions] = useState<{ width: number; height: number }>({ width: 360, height: 202 });

  useEffect(() => {
    const el = playerContainerRef.current;
    if (!el) return;
    const update = () => {
      if (el.clientWidth > 0 && el.clientHeight > 0) {
        setMobileContainerDimensions({ width: el.clientWidth, height: el.clientHeight });
      }
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, [isFullscreen, isLandscape, selectedFeed]);

  const mobileAspectRatio = useMemo(() => {
    return (
      videoAspectRatio
      || (videoRef.current?.videoWidth && videoRef.current?.videoHeight
        ? videoRef.current.videoWidth / videoRef.current.videoHeight
        : null)
      || (storyboardMetadata?.tileWidth && storyboardMetadata?.tileHeight
        ? storyboardMetadata.tileWidth / storyboardMetadata.tileHeight
        : null)
      || (selectedFeed === 'pano' ? (3840 / 1536) : (16 / 9))
    );
  }, [videoAspectRatio, selectedFeed, storyboardMetadata, videoRef]);

  const mobileOverlayDimensions = useMemo(() => {
    const containerW = mobileContainerDimensions.width || 360;
    const containerH = mobileContainerDimensions.height || 202;
    const containerAspect = containerW / containerH;
    let targetW = containerW;
    let targetH = containerH;
    if (containerAspect > mobileAspectRatio) {
      targetH = containerH;
      targetW = Math.round(containerH * mobileAspectRatio);
    } else {
      targetW = containerW;
      targetH = Math.round(containerW / mobileAspectRatio);
    }
    return { width: targetW, height: targetH };
  }, [mobileContainerDimensions, mobileAspectRatio]);

  // Compute maximum allowable pan bounds for current zoom level so video edges don't leave container
  const getMaxPan = useCallback((currentZoom: number) => {
    const W = mobileOverlayDimensions.width || 360;
    const H = mobileOverlayDimensions.height || 202;
    return {
      x: Math.max(0, (W * (currentZoom - 1)) / 2),
      y: Math.max(0, (H * (currentZoom - 1)) / 2),
    };
  }, [mobileOverlayDimensions]);

  // Reset video zoom back to 1.0x
  const handleResetZoom = useCallback(() => {
    setZoom(1.0);
    setPan({ x: 0, y: 0 });
  }, []);

  // Prevent browser viewport zoom on mobile when pinching or panning on the video player
  useEffect(() => {
    const el = playerContainerRef.current;
    if (!el) return;

    const preventDefault = (e: Event) => {
      e.preventDefault();
    };

    const handleTouchMoveNative = (e: TouchEvent) => {
      if (e.touches.length >= 2 || (zoom > 1.05 && touchStateRef.current.isPanning)) {
        e.preventDefault();
      }
    };

    el.addEventListener('gesturestart', preventDefault, { passive: false });
    el.addEventListener('gesturechange', preventDefault, { passive: false });
    el.addEventListener('touchmove', handleTouchMoveNative, { passive: false });

    return () => {
      el.removeEventListener('gesturestart', preventDefault);
      el.removeEventListener('gesturechange', preventDefault);
      el.removeEventListener('touchmove', handleTouchMoveNative);
    };
  }, [zoom]);

  const handleToggleFullscreen = () => {
    if (!playerContainerRef.current) return;
    if (!document.fullscreenElement) {
      if (playerContainerRef.current.requestFullscreen) {
        playerContainerRef.current.requestFullscreen({ navigationUI: 'hide' }).catch(() => {});
      } else if ((playerContainerRef.current as any).webkitRequestFullscreen) {
        (playerContainerRef.current as any).webkitRequestFullscreen();
      } else if ((videoRef.current as any)?.webkitEnterFullscreen) {
        (videoRef.current as any).webkitEnterFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      }
    }
  };

  const isInteractiveElement = (target: EventTarget | null): boolean => {
    if (!target || !(target instanceof Element)) return false;
    return Boolean(
      target.closest('button, [role="button"], [role="slider"], .MuiSlider-root, .MuiMenu-root, .MuiMenuItem-root, .MuiBackdrop-root, a')
    );
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isInteractiveElement(e.target)) return;

    touchHandledRef.current = true;
    const state = touchStateRef.current;

    if (e.touches.length === 2) {
      // Two fingers: Pinch-to-zoom!
      if (state.singleTapTimeout) {
        clearTimeout(state.singleTapTimeout);
        state.singleTapTimeout = null;
      }
      state.isPinching = true;
      state.isPanning = false;
      state.hasPanned = true;
      setIsInteracting(true);

      const t1 = e.touches[0];
      const t2 = e.touches[1];
      state.startPinchDist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
      state.startZoom = zoom;
      state.startPinchMid = {
        x: (t1.clientX + t2.clientX) / 2,
        y: (t1.clientY + t2.clientY) / 2,
      };
      state.startPan = { ...pan };
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      state.isPinching = false;
      state.touchStart = { x: t.clientX, y: t.clientY };
      state.hasPanned = false;

      if (zoom > 1.05) {
        state.isPanning = true;
        state.startPanSingle = { ...pan };
        setIsInteracting(true);
      } else {
        state.isPanning = false;
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isInteractiveElement(e.target)) return;

    const state = touchStateRef.current;

    if (state.isPinching && e.touches.length === 2) {
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const dist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
      if (state.startPinchDist > 0) {
        const scaleDelta = dist / state.startPinchDist;
        const nextZoom = Math.max(1.0, Math.min(5.0, parseFloat((state.startZoom * scaleDelta).toFixed(2))));
        setZoom(nextZoom);

        const currentMid = {
          x: (t1.clientX + t2.clientX) / 2,
          y: (t1.clientY + t2.clientY) / 2,
        };
        const deltaX = currentMid.x - state.startPinchMid.x;
        const deltaY = currentMid.y - state.startPinchMid.y;

        const maxPan = getMaxPan(nextZoom);
        setPan({
          x: Math.max(-maxPan.x, Math.min(maxPan.x, state.startPan.x + deltaX)),
          y: Math.max(-maxPan.y, Math.min(maxPan.y, state.startPan.y + deltaY)),
        });
      }
    } else if (state.isPanning && e.touches.length === 1 && zoom > 1.05) {
      const t = e.touches[0];
      const dx = t.clientX - state.touchStart.x;
      const dy = t.clientY - state.touchStart.y;

      if (Math.hypot(dx, dy) > 8) {
        state.hasPanned = true;
        const maxPan = getMaxPan(zoom);
        setPan({
          x: Math.max(-maxPan.x, Math.min(maxPan.x, state.startPanSingle.x + dx)),
          y: Math.max(-maxPan.y, Math.min(maxPan.y, state.startPanSingle.y + dy)),
        });
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isInteractiveElement(e.target)) return;

    setIsInteracting(false);
    const state = touchStateRef.current;

    if (state.isPinching) {
      state.isPinching = false;
      if (zoom < 1.05) {
        setZoom(1.0);
        setPan({ x: 0, y: 0 });
      }
      return;
    }

    if (state.isPanning && state.hasPanned) {
      state.isPanning = false;
      state.hasPanned = false;
      return;
    }
    state.isPanning = false;

    if (e.changedTouches.length === 0) return;
    const touch = e.changedTouches[0];
    const rect = playerContainerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    const width = rect.width;
    const height = rect.height;

    if (x < 0 || x > width || y < 0 || y > height) return;

    const now = Date.now();
    const timeSinceLastTap = now - state.lastTapTime;
    const distSinceLastTap = Math.hypot(x - state.lastTapPos.x, y - state.lastTapPos.y);

    if (timeSinceLastTap < 300 && distSinceLastTap < 100) {
      // Double tap detected!
      if (state.singleTapTimeout) {
        clearTimeout(state.singleTapTimeout);
        state.singleTapTimeout = null;
      }
      state.lastTapTime = 0;

      if (x < width * 0.42) {
        // Double-tap left side: Rewind 10 seconds
        onRelativeSeek(-10);
        setSkipFeedback({ direction: 'left', id: Date.now() });
        setShowControls(true);
        setTimeout(() => setSkipFeedback((prev) => (prev?.direction === 'left' ? null : prev)), 800);
      } else if (x > width * 0.58) {
        // Double-tap right side: Fast forward 10 seconds
        onRelativeSeek(10);
        setSkipFeedback({ direction: 'right', id: Date.now() });
        setShowControls(true);
        setTimeout(() => setSkipFeedback((prev) => (prev?.direction === 'right' ? null : prev)), 800);
      } else {
        // Center double-tap: Toggle Play
        onTogglePlay();
      }
    } else {
      // First tap: Debounce single-tap to toggle controls
      state.lastTapTime = now;
      state.lastTapPos = { x, y };

      state.singleTapTimeout = setTimeout(() => {
        state.singleTapTimeout = null;
        setShowControls((prev) => !prev);
      }, 280);
    }
  };

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isInteractiveElement(e.target)) return;
    if (touchHandledRef.current) {
      touchHandledRef.current = false;
      return;
    }

    const rect = playerContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const now = Date.now();
    const state = touchStateRef.current;
    const timeSinceLastTap = now - state.lastTapTime;

    if (timeSinceLastTap < 300) {
      if (state.singleTapTimeout) {
        clearTimeout(state.singleTapTimeout);
        state.singleTapTimeout = null;
      }
      state.lastTapTime = 0;

      if (x < rect.width * 0.42) {
        onRelativeSeek(-10);
        setSkipFeedback({ direction: 'left', id: Date.now() });
        setShowControls(true);
        setTimeout(() => setSkipFeedback((prev) => (prev?.direction === 'left' ? null : prev)), 800);
      } else if (x > rect.width * 0.58) {
        onRelativeSeek(10);
        setSkipFeedback({ direction: 'right', id: Date.now() });
        setShowControls(true);
        setTimeout(() => setSkipFeedback((prev) => (prev?.direction === 'right' ? null : prev)), 800);
      } else {
        onTogglePlay();
      }
    } else {
      state.lastTapTime = now;
      state.lastTapPos = { x, y: e.clientY - rect.top };
      state.singleTapTimeout = setTimeout(() => {
        state.singleTapTimeout = null;
        setShowControls((prev) => !prev);
      }, 280);
    }
  };

  const currentReelClip = isReelMode && highlights[currentReelIndex] ? highlights[currentReelIndex] : null;

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#09090b',
        color: '#ffffff',
        pb: 4,
      }}
    >
      {/* Top Mobile App Bar */}
      <Box
        sx={{
          display: isFillScreen ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1,
          bgcolor: '#141416',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          zIndex: 10,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/highlights')}
          size="small"
          sx={{
            color: '#ffffff',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.85rem',
            p: 0.5,
          }}
        >
          All Games
        </Button>

        <Stack direction="row" spacing={1} alignItems="center">
          {/* Feed Switcher (if both Auto and Pano feeds available) */}
          {!isReelMode && (hasAuto || hasPano) && (
            <Stack
              direction="row"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.08)',
                borderRadius: 1.5,
                p: 0.25,
                border: '1px solid rgba(255, 255, 255, 0.12)',
              }}
            >
              <Button
                size="small"
                disabled={!hasAuto}
                onClick={() => onFeedChange('auto')}
                sx={{
                  color: '#fff',
                  px: 1,
                  py: 0.2,
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  minWidth: 'auto',
                  borderRadius: 1,
                  bgcolor: selectedFeed === 'auto' ? 'primary.main' : 'transparent',
                  '&:hover': { bgcolor: selectedFeed === 'auto' ? 'primary.dark' : 'rgba(255,255,255,0.05)' },
                }}
              >
                Auto
              </Button>
              <Button
                size="small"
                disabled={!hasPano}
                onClick={() => onFeedChange('pano')}
                sx={{
                  color: '#fff',
                  px: 1,
                  py: 0.2,
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  minWidth: 'auto',
                  borderRadius: 1,
                  bgcolor: selectedFeed === 'pano' ? 'primary.main' : 'transparent',
                  '&:hover': { bgcolor: selectedFeed === 'pano' ? 'primary.dark' : 'rgba(255,255,255,0.05)' },
                }}
              >
                Pano
              </Button>
            </Stack>
          )}

          {/* Playback Speed Picker */}
          <Button
            size="small"
            startIcon={<SpeedIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => setSpeedAnchorEl(e.currentTarget)}
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              textTransform: 'none',
              fontSize: '0.75rem',
              fontWeight: 600,
              px: 0.75,
              py: 0.4,
              minWidth: 'auto',
              bgcolor: 'rgba(255, 255, 255, 0.08)',
              borderRadius: 1.5,
            }}
          >
            {playbackRate}x
          </Button>

          <Menu
            anchorEl={speedAnchorEl}
            open={Boolean(speedAnchorEl)}
            onClose={() => setSpeedAnchorEl(null)}
            PaperProps={{
              sx: { bgcolor: '#1c1c1f', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' },
            }}
          >
            {[0.75, 1.0, 1.25, 1.5, 2.0].map((rate) => (
              <MenuItem
                key={rate}
                selected={playbackRate === rate}
                onClick={() => {
                  onSetPlaybackRate(rate);
                  setSpeedAnchorEl(null);
                }}
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: playbackRate === rate ? 700 : 400,
                  '&.Mui-selected': { bgcolor: 'primary.dark' },
                }}
              >
                {rate}x Speed
              </MenuItem>
            ))}
          </Menu>

          {/* Share Link at Current Time */}
          <IconButton
            size="small"
            onClick={onShareCurrentTime}
            aria-label="Share video link"
            sx={{ color: '#fff', p: 0.6, bgcolor: 'rgba(255, 255, 255, 0.08)' }}
          >
            <ShareIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Reel Mode Active Banner */}
      {isReelMode && currentReelClip && !isFillScreen && (
        <Box
          sx={{
            bgcolor: 'primary.dark',
            color: '#fff',
            px: 1.5,
            py: 0.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 10,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <MovieFilterIcon sx={{ fontSize: 18 }} />
            <Typography variant="caption" fontWeight={600}>
              Highlight {currentReelIndex + 1} of {highlights.length}: {currentReelClip.playDescription || `Goal #${currentReelClip.goalNumber}`}
            </Typography>
          </Stack>
          <Button
            size="small"
            startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
            onClick={onExitReelMode}
            sx={{ color: '#fff', textTransform: 'none', fontSize: '0.72rem', py: 0.1, px: 0.8 }}
          >
            Exit Reel
          </Button>
        </Box>
      )}

      {/* ========================================================
          STICKY 16:9 / FULL-BLEED VIDEO CONTAINER (TOUCH-OPTIMIZED)
          ======================================================== */}
      <Box
        ref={playerContainerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleContainerClick}
        sx={{
          position: isFillScreen ? 'fixed' : 'sticky',
          top: 0,
          left: 0,
          right: isFillScreen ? 0 : 'auto',
          bottom: isFillScreen ? 0 : 'auto',
          width: isFillScreen ? '100vw' : '100%',
          height: isFillScreen ? '100dvh' : 'auto',
          aspectRatio: isFillScreen ? 'auto' : '16 / 9',
          maxHeight: isFillScreen ? '100dvh' : 'none',
          bgcolor: '#000000',
          zIndex: isFillScreen ? 9999 : 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        {/* Floating Video Zoom Badge (Pinch-to-zoom active indicator & reset) */}
        {zoom > 1.05 && (
          <Button
            size="small"
            startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => {
              e.stopPropagation();
              handleResetZoom();
            }}
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              bgcolor: 'rgba(0, 0, 0, 0.75)',
              color: '#38bdf8',
              border: '1px solid rgba(56, 189, 248, 0.4)',
              backdropFilter: 'blur(8px)',
              borderRadius: 4,
              px: 1.25,
              py: 0.25,
              fontSize: '0.72rem',
              fontWeight: 700,
              textTransform: 'none',
              zIndex: 66,
              boxShadow: '0 2px 8px rgba(0,0,0,0.6)',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.9)',
              },
            }}
          >
            {zoom.toFixed(1)}x • Reset
          </Button>
        )}

        {/* HTML5 Native Video Element */}
        <video
          ref={videoRef}
          src={videoStreamUrl}
          crossOrigin="anonymous"
          playsInline
          // @ts-ignore
          webkit-playsinline="true"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: 'center center',
            transition: isInteracting ? 'none' : 'transform 0.15s ease-out',
            imageRendering: zoom >= 2.5 ? 'pixelated' : 'auto',
          }}
          onTimeUpdate={onTimeUpdate}
          onSeeking={onSeeking}
          onSeeked={onSeeked}
          onWaiting={onWaiting}
          onPlaying={onPlaying}
          onCanPlay={onCanPlay}
          onLoadedMetadata={onLoadedMetadata}
          onError={onVideoPlaybackError}
          onEnded={onVideoEnded}
        />

        {/* Ultra-Dense GOP Scrub Proxy Video for Instant Mobile Seeking */}
        {scrubStreamUrl && !isReelMode && (
          <video
            ref={scrubVideoRef}
            src={scrubStreamUrl}
            crossOrigin="anonymous"
            muted
            playsInline
            // @ts-ignore
            webkit-playsinline="true"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
              pointerEvents: 'none',
              opacity: (isScrubbing || isScrubbingUI) ? 1 : 0,
              visibility: (isScrubbing || isScrubbingUI) ? 'visible' : 'hidden',
              zIndex: 52,
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: 'center center',
              transition: isInteracting ? 'none' : 'transform 0.15s ease-out',
              imageRendering: zoom >= 2.5 ? 'pixelated' : 'auto',
            }}
          />
        )}

        {/* Scaled-up Blurred Storyboard Overlay during Mobile Scrubbing */}
        {storyboardMetadata && game?.gameId && isScrubbing && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              zIndex: 53,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: mobileOverlayDimensions.width,
                height: mobileOverlayDimensions.height,
                aspectRatio: `${mobileAspectRatio}`,
                position: 'relative',
                filter: 'blur(10px)',
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
                transition: isInteracting ? 'none' : 'transform 0.15s ease-out',
              }}
            >
              <StoryboardPreview
                gameId={game.gameId}
                feed={selectedFeed || 'auto'}
                time={Math.max(0, localScrubTime + (game?.feedSync?.offsets?.[selectedFeed] ?? 0))}
                metadata={storyboardMetadata}
                authToken={authToken}
                width="100%"
                height="100%"
                border="none"
                borderRadius={0}
                boxShadow="none"
                showTimecode={false}
              />
            </Box>
          </Box>
        )}

        {/* Double-Tap Skip Feedback Ripple Indicator */}
        {skipFeedback && (
          <Fade in={Boolean(skipFeedback)} timeout={200}>
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: skipFeedback.direction === 'left' ? 0 : 'auto',
                right: skipFeedback.direction === 'right' ? 0 : 'auto',
                width: '45%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0, 0, 0, 0.55)',
                borderTopRightRadius: skipFeedback.direction === 'left' ? '50% 100%' : 0,
                borderBottomRightRadius: skipFeedback.direction === 'left' ? '50% 100%' : 0,
                borderTopLeftRadius: skipFeedback.direction === 'right' ? '50% 100%' : 0,
                borderBottomLeftRadius: skipFeedback.direction === 'right' ? '50% 100%' : 0,
                pointerEvents: 'none',
                zIndex: 65,
              }}
            >
              {skipFeedback.direction === 'left' ? (
                <>
                  <Replay10Icon sx={{ fontSize: 44, color: '#38bdf8' }} />
                  <Typography variant="body2" fontWeight={700} sx={{ color: '#38bdf8', mt: 0.5 }}>
                    -10 seconds
                  </Typography>
                </>
              ) : (
                <>
                  <Forward10Icon sx={{ fontSize: 44, color: '#38bdf8' }} />
                  <Typography variant="body2" fontWeight={700} sx={{ color: '#38bdf8', mt: 0.5 }}>
                    +10 seconds
                  </Typography>
                </>
              )}
            </Box>
          </Fade>
        )}

        {/* Delayed Seek / Loading & Buffering Feedback (No blur, crisp frame remains visible) */}
        {(isDelayedSeeking || isVideoBuffering) && !isScrubbing && !isScrubbingUI && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              bgcolor: 'rgba(0, 0, 0, 0.78)',
              borderRadius: 3,
              px: 2.5,
              py: 1.2,
              zIndex: 65,
              pointerEvents: 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
            }}
          >
            <CircularProgress size={20} sx={{ color: '#38bdf8' }} thickness={4} />
            <Typography
              variant="body2"
              sx={{
                color: '#fff',
                fontWeight: 600,
                letterSpacing: 0.5,
                textShadow: '0 1px 3px rgba(0,0,0,0.8)',
              }}
            >
              {isDelayedSeeking && delayedSeekTime && delayedSeekTime > 0
                ? `Loading frame (${formatSeconds(delayedSeekTime)})...`
                : isDelayedSeeking
                ? 'Loading frame...'
                : 'Buffering stream...'}
            </Typography>
          </Box>
        )}

        {/* Overlay Controls (Persist until tapped outside buttons) */}
        <Fade in={showControls} timeout={250}>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(0, 0, 0, 0.45)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              p: 1.5,
              zIndex: 58,
              pointerEvents: showControls ? 'auto' : 'none',
            }}
          >
            {/* Top Bar inside Overlay */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                {isFillScreen && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleFullscreen();
                    }}
                    aria-label="Exit fullscreen"
                    sx={{ color: '#fff', bgcolor: 'rgba(0,0,0,0.6)', p: 0.75 }}
                  >
                    <FullscreenExitIcon fontSize="small" />
                  </IconButton>
                )}
                {isReelMode ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton
                      size="small"
                      disabled={currentReelIndex === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPlayHighlightClip(currentReelIndex - 1);
                      }}
                      sx={{ color: '#fff', bgcolor: 'rgba(0,0,0,0.6)' }}
                    >
                      <SkipPreviousIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
                      {currentReelIndex + 1} / {highlights.length}
                    </Typography>
                    <IconButton
                      size="small"
                      disabled={currentReelIndex >= highlights.length - 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPlayHighlightClip(currentReelIndex + 1);
                      }}
                      sx={{ color: '#fff', bgcolor: 'rgba(0,0,0,0.6)' }}
                    >
                      <SkipNextIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ) : (
                  <Box />
                )}
              </Stack>

              {/* Right Controls in Overlay Top: Feed Switcher & Speed (visible in Fill-Screen) + Fullscreen Button */}
              <Stack direction="row" spacing={1} alignItems="center">
                {!isReelMode && (hasAuto || hasPano) && isFillScreen && (
                  <Stack
                    direction="row"
                    sx={{
                      bgcolor: 'rgba(0, 0, 0, 0.6)',
                      borderRadius: 1.5,
                      p: 0.25,
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                    }}
                  >
                    <Button
                      size="small"
                      disabled={!hasAuto}
                      onClick={(e) => {
                        e.stopPropagation();
                        onFeedChange('auto');
                      }}
                      sx={{
                        color: '#fff',
                        px: 1,
                        py: 0.2,
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        minWidth: 'auto',
                        borderRadius: 1,
                        bgcolor: selectedFeed === 'auto' ? 'primary.main' : 'transparent',
                      }}
                    >
                      Auto
                    </Button>
                    <Button
                      size="small"
                      disabled={!hasPano}
                      onClick={(e) => {
                        e.stopPropagation();
                        onFeedChange('pano');
                      }}
                      sx={{
                        color: '#fff',
                        px: 1,
                        py: 0.2,
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        minWidth: 'auto',
                        borderRadius: 1,
                        bgcolor: selectedFeed === 'pano' ? 'primary.main' : 'transparent',
                      }}
                    >
                      Pano
                    </Button>
                  </Stack>
                )}

                {isFillScreen && (
                  <Button
                    size="small"
                    startIcon={<SpeedIcon sx={{ fontSize: 16 }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSpeedAnchorEl(e.currentTarget);
                    }}
                    sx={{
                      color: '#fff',
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      px: 0.75,
                      py: 0.4,
                      minWidth: 'auto',
                      bgcolor: 'rgba(0, 0, 0, 0.6)',
                      borderRadius: 1.5,
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                    }}
                  >
                    {playbackRate}x
                  </Button>
                )}

                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleFullscreen();
                  }}
                  aria-label="Toggle fullscreen"
                  sx={{
                    color: '#fff',
                    bgcolor: 'rgba(0,0,0,0.6)',
                    p: 0.75,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                  }}
                >
                  {isFillScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Stack>
            </Box>

            {/* Center Transport Controls: -10s, Play/Pause, +10s */}
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              justifyContent="center"
              sx={{ width: '100%' }}
            >
              {/* -10 Second Skip */}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onRelativeSeek(-10);
                }}
                aria-label="Rewind 10 seconds"
                sx={{
                  color: '#ffffff',
                  bgcolor: 'rgba(0, 0, 0, 0.65)',
                  p: 1.25,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.85)' },
                }}
              >
                <Replay10Icon sx={{ fontSize: 30 }} />
              </IconButton>

              {/* Play / Pause Toggle (Large Center Button) */}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onTogglePlay();
                }}
                aria-label={isPlaying ? 'Pause' : 'Play'}
                sx={{
                  bgcolor: 'primary.main',
                  color: '#ffffff',
                  p: 2,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.5)',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                {isPlaying ? <PauseIcon sx={{ fontSize: 36 }} /> : <PlayArrowIcon sx={{ fontSize: 36 }} />}
              </IconButton>

              {/* +10 Second Skip */}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onRelativeSeek(10);
                }}
                aria-label="Forward 10 seconds"
                sx={{
                  color: '#ffffff',
                  bgcolor: 'rgba(0, 0, 0, 0.65)',
                  p: 1.25,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.85)' },
                }}
              >
                <Forward10Icon sx={{ fontSize: 30 }} />
              </IconButton>
            </Stack>

            {/* Bottom Scrubber & Timecode */}
            <Box sx={{ width: '100%', pt: 1 }}>
              {/* Monospace Timecode */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25, px: 0.5 }}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#38bdf8' }}>
                  {formatSeconds(isScrubbing ? localScrubTime : currentTime)}
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.7)' }}>
                  {formatSeconds(duration)}
                </Typography>
              </Box>

              {/* Touch Scrubber with Goal Markers & Floating Thumbnail */}
              <Box sx={{ position: 'relative', width: '100%', px: 0.5 }}>
                {isScrubbing && game?.gameId && storyboardMetadata && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 40,
                      left: `${Math.max(16, Math.min(84, (localScrubTime / (duration || 1)) * 100))}%`,
                      transform: 'translateX(-50%)',
                      zIndex: 80,
                      pointerEvents: 'none',
                    }}
                  >
                    <StoryboardPreview
                      gameId={game.gameId}
                      feed={selectedFeed || 'auto'}
                      time={Math.max(0, localScrubTime + (game?.feedSync?.offsets?.[selectedFeed] ?? 0))}
                      metadata={storyboardMetadata}
                      authToken={authToken}
                      width={Math.round(73 * mobileAspectRatio)}
                      height={73}
                      showTimecode={true}
                    />
                  </Box>
                )}
                <Slider
                  size="small"
                  value={isScrubbing ? localScrubTime : currentTime}
                  min={0}
                  max={duration || 100}
                  onChange={(_, val) => {
                    const t = val as number;
                    setIsScrubbing(true);
                    setLocalScrubTime(t);
                    onSeek(t, true);
                  }}
                  onChangeCommitted={(_, val) => {
                    const t = val as number;
                    setIsScrubbing(false);
                    onSeek(t, false);
                  }}
                  aria-label="Video scrubber"
                  sx={{
                    color: 'primary.main',
                    height: 6,
                    py: 1,
                    '& .MuiSlider-thumb': {
                      width: 16,
                      height: 16,
                      bgcolor: '#fff',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.6)',
                      '&:hover, &.Mui-focusVisible': {
                        boxShadow: '0 0 0 6px rgba(56, 189, 248, 0.25)',
                      },
                    },
                    '& .MuiSlider-track': {
                      bgcolor: 'primary.main',
                      border: 'none',
                    },
                    '& .MuiSlider-rail': {
                      bgcolor: 'rgba(255, 255, 255, 0.35)',
                      opacity: 1,
                    },
                  }}
                />

                {/* Visual Goal Marker Pips on Slider Track */}
                {duration > 0 &&
                  sortedGoals.map((g, idx) => {
                    const pct = Math.min(100, Math.max(0, (g.videoTimestamp / duration) * 100));
                    const isJets = isGoalByJets(g, isJetsHome, game?.targetTeam);
                    const isNear = Math.abs(currentTime - g.videoTimestamp) < 15;

                    return (
                      <Box
                        key={`goal-pip-${idx}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onJumpToGoal(g.videoTimestamp);
                        }}
                        sx={{
                          position: 'absolute',
                          left: `${pct}%`,
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: isNear ? 10 : 6,
                          height: isNear ? 10 : 6,
                          borderRadius: '50%',
                          bgcolor: isJets ? '#38bdf8' : '#f87171',
                          border: isNear ? '2px solid #ffffff' : '1px solid rgba(0,0,0,0.8)',
                          boxShadow: isNear ? '0 0 6px #38bdf8' : 'none',
                          zIndex: 3,
                          cursor: 'pointer',
                          pointerEvents: 'auto',
                          transition: 'all 0.15s ease',
                        }}
                      />
                    );
                  })}
              </Box>
            </Box>
          </Box>
        </Fade>
      </Box>

      {/* ========================================================
          SCROLLABLE CONTENT AREA BELOW VIDEO (PORTRAIT)
          ======================================================== */}
      <Box sx={{ px: 2, pt: 2, display: isFillScreen ? 'none' : 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Game Title & Information Header */}
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#ffffff', lineHeight: 1.2 }}>
            {focalTeamFull} {isJetsHome ? 'vs' : 'at'} {game.opponent || 'Opponent'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mt: 0.5 }}>
            {game.date} • {game.rinkName} {game.sheetName && `(${game.sheetName})`}
          </Typography>
        </Box>

        {/* Quick Period Jumpers */}
        <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.04)', borderRadius: 2, p: 1.25 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ overflowX: 'auto', scrollbarWidth: 'none' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 600, flexShrink: 0, mr: 0.5 }}>
              Period:
            </Typography>
            {[1, 2, 3].map((pNum) => (
              <Chip
                key={pNum}
                label={`Period ${pNum}`}
                size="small"
                onClick={() => onJumpToPeriod(pNum)}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'primary.main' },
                }}
              />
            ))}
            {game.timeline?.overtimeStart && (
              <Chip
                label="Overtime"
                size="small"
                onClick={() => onJumpToPeriod(4)}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'primary.main' },
                }}
              />
            )}
          </Stack>
        </Box>

        {/* Tabs: Goals vs Highlights */}
        <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            variant="fullWidth"
            sx={{
              minHeight: 40,
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.65)',
                fontWeight: 600,
                fontSize: '0.85rem',
                textTransform: 'none',
                minHeight: 40,
                '&.Mui-selected': { color: '#38bdf8' },
              },
            }}
          >
            <Tab label={`Goal Markers (${sortedGoals.length})`} />
            <Tab label={`Highlight Clips (${highlights.length})`} />
            <Tab label={`Notes & Clips (${sortedTimelineEvents.length})`} />
          </Tabs>
        </Box>

        {/* Tab 0: Chronologically Sorted Goal Markers */}
        {activeTab === 0 && (
          <Stack spacing={1.5}>
            {sortedGoals.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'rgba(255, 255, 255, 0.02)', borderRadius: 2 }}>
                <SportsHockeyIcon sx={{ fontSize: 36, color: 'rgba(255, 255, 255, 0.3)', mb: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  No goals recorded for this game.
                </Typography>
              </Box>
            ) : (
              sortedGoals.map((g, idx) => {
                const isJets = isGoalByJets(g, isJetsHome, game?.targetTeam);
                const teamLabel = getGoalTeamName(g, isJetsHome, game?.opponent, game?.targetTeam);
                const isCurrentlyPlaying =
                  !isReelMode &&
                  isPlaying &&
                  currentTime >= (g.videoTimestamp || 0) - 10 &&
                  currentTime <= (g.videoTimestamp || 0) + 20;

                const scorerInfo = g.scorer
                  ? `${g.scorerJersey ? `#${g.scorerJersey} ` : ''}${g.scorer}`
                  : g.scorerJersey
                  ? `#${g.scorerJersey}`
                  : null;

                const assistInfo = g.assists && g.assists.length > 0 ? g.assists.join(', ') : null;

                return (
                  <Paper
                    key={`mobile-goal-${idx}`}
                    elevation={0}
                    onClick={() => onJumpToGoal(g.videoTimestamp)}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: isCurrentlyPlaying ? 'rgba(56, 189, 248, 0.12)' : '#18181b',
                      border: isCurrentlyPlaying
                        ? '1.5px solid #38bdf8'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease-in-out',
                      '&:active': {
                        bgcolor: 'rgba(56, 189, 248, 0.2)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={teamLabel}
                          size="small"
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            bgcolor: isJets ? 'primary.main' : 'rgba(255, 255, 255, 0.12)',
                            color: '#ffffff',
                          }}
                        />

                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#ffffff' }}>
                          Goal #{g.goalNumber || idx + 1}
                        </Typography>

                        {g.scoreAfter && (
                          <Chip
                            label={g.scoreAfter}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              borderColor: 'rgba(255, 255, 255, 0.3)',
                              color: 'rgba(255, 255, 255, 0.9)',
                            }}
                          />
                        )}
                      </Stack>

                      {/* Video timestamp */}
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          color: '#38bdf8',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                        }}
                      >
                        {formatSeconds(g.videoTimestamp)}
                      </Typography>
                    </Box>

                    {/* Scorer and Assist Information */}
                    {scorerInfo && (
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#ffffff', mb: 0.25 }}>
                        Scorer: <Box component="span" sx={{ color: '#38bdf8' }}>{scorerInfo}</Box>
                      </Typography>
                    )}

                    {assistInfo && (
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)', display: 'block', mb: 0.5 }}>
                        Assists: {assistInfo}
                      </Typography>
                    )}

                    {/* Period & Game Clock */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                        Period {g.period || 1} {g.gameClock && `• Clock: ${g.gameClock}`}
                        {g.description && ` • ${g.description}`}
                      </Typography>

                      <Button
                        size="small"
                        startIcon={<PlayArrowIcon />}
                        sx={{
                          fontSize: '0.72rem',
                          py: 0.2,
                          px: 1,
                          color: '#38bdf8',
                          bgcolor: 'rgba(56, 189, 248, 0.1)',
                          textTransform: 'none',
                          borderRadius: 1,
                          '&:hover': { bgcolor: 'rgba(56, 189, 248, 0.2)' },
                        }}
                      >
                        Jump to Goal
                      </Button>
                    </Box>
                  </Paper>
                );
              })
            )}
          </Stack>
        )}

        {/* Tab 1: Highlight Clips (Reel Mode Gallery) */}
        {activeTab === 1 && (
          <Stack spacing={1.5}>
            {highlights.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'rgba(255, 255, 255, 0.02)', borderRadius: 2 }}>
                <MovieFilterIcon sx={{ fontSize: 36, color: 'rgba(255, 255, 255, 0.3)', mb: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  No pre-generated highlight clips found.
                </Typography>
              </Box>
            ) : (
              highlights.map((hl, idx) => (
                <Paper
                  key={`mobile-clip-${idx}`}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: isReelMode && currentReelIndex === idx ? 'rgba(56, 189, 248, 0.12)' : '#18181b',
                    border:
                      isReelMode && currentReelIndex === idx
                        ? '1.5px solid #38bdf8'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#ffffff' }}>
                      {hl.playDescription || `${getGoalTeamName(hl, isJetsHome, game?.opponent, game?.targetTeam)} Goal #${hl.goalNumber || idx + 1}`}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                      Duration: {hl.durationSeconds ? `${hl.durationSeconds}s` : 'Clip'} • {formatSeconds(hl.videoTimestamp)}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => onPlayHighlightClip(idx)}
                      aria-label={`Play clip ${hl.playDescription || hl.goalNumber || idx + 1}`}
                      sx={{
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        px: 1.25,
                        py: 0.4,
                      }}
                    >
                      Play Clip
                    </Button>
                    {onDownloadHighlightClip && (
                      <IconButton
                        size="small"
                        onClick={() => onDownloadHighlightClip(hl)}
                        aria-label="Download clip"
                        sx={{ color: 'rgba(255, 255, 255, 0.7)', bgcolor: 'rgba(255, 255, 255, 0.08)' }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Paper>
              ))
            )}
          </Stack>
        )}

        {/* Tab 2: Notes & Custom Clips */}
        {activeTab === 2 && (
          <Stack spacing={1.5}>
            {sortedTimelineEvents.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'rgba(255, 255, 255, 0.02)', borderRadius: 2 }}>
                <StickyNote2Icon sx={{ fontSize: 36, color: 'rgba(255, 255, 255, 0.3)', mb: 1 }} />
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  No notes or custom clips created yet.
                </Typography>
              </Box>
            ) : (
              sortedTimelineEvents.map((evt) => {
                if (evt.eventType === 'note') {
                  const note = evt as TimelineNote;
                  return (
                    <Paper
                      key={`mobile-note-${note.id}`}
                      elevation={0}
                      onClick={() => onSeek(note.time)}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: '#18181b',
                        border: '1px solid rgba(245, 158, 11, 0.35)',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease-in-out',
                        '&:active': { bgcolor: 'rgba(245, 158, 11, 0.1)' },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            icon={<StickyNote2Icon sx={{ fontSize: '13px !important' }} />}
                            label="Note"
                            size="small"
                            sx={{ height: 22, fontWeight: 700, fontSize: '0.72rem', bgcolor: '#f59e0b', color: '#000' }}
                          />
                          <Chip
                            label={note.generalAccess === 'public' ? 'Public' : (note.shares && note.shares.length > 0 ? 'Shared' : 'Private')}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.66rem', color: 'rgba(255, 255, 255, 0.7)', borderColor: 'rgba(255, 255, 255, 0.2)' }}
                          />
                        </Stack>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#f59e0b', fontWeight: 700, fontSize: '0.8rem' }}>
                          {formatSeconds(note.time)}
                        </Typography>
                      </Box>
                      {note.text ? (
                        <Typography variant="body2" sx={{ color: '#ffffff', mb: 0.5, whiteSpace: 'pre-wrap' }}>
                          {note.text}
                        </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', mb: 0.5 }}>
                          (Empty note)
                        </Typography>
                      )}
                      {note.creatorEmail && (
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mb: 1 }}>
                          By: {note.creatorEmail}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                        <Button
                          size="small"
                          startIcon={<PlayArrowIcon />}
                          sx={{
                            fontSize: '0.72rem',
                            py: 0.2,
                            px: 1,
                            color: '#f59e0b',
                            bgcolor: 'rgba(245, 158, 11, 0.1)',
                            textTransform: 'none',
                            borderRadius: 1,
                          }}
                        >
                          Jump to Note
                        </Button>
                      </Box>
                    </Paper>
                  );
                } else {
                  const clip = evt as TimelineClip;
                  const dur = clip.endTime - clip.startTime;
                  return (
                    <Paper
                      key={`mobile-clip-${clip.id}`}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: '#18181b',
                        border: '1px solid rgba(56, 189, 248, 0.35)',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            icon={<ContentCutIcon sx={{ fontSize: '13px !important' }} />}
                            label="Clip"
                            size="small"
                            sx={{ height: 22, fontWeight: 700, fontSize: '0.72rem', bgcolor: '#38bdf8', color: '#000' }}
                          />
                          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#ffffff' }}>
                            {clip.title || 'Custom Clip'}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#38bdf8', fontWeight: 700, fontSize: '0.8rem' }}>
                          {formatSeconds(clip.startTime)} → {formatSeconds(clip.endTime)}
                        </Typography>
                      </Box>
                      {clip.notes && (
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 0.5 }}>
                          {clip.notes}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mb: 1 }}>
                        Duration: {dur > 0 ? `${dur.toFixed(1)}s` : ''} • {clip.creatorEmail ? `By: ${clip.creatorEmail}` : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => {
                            onSeek(clip.startTime);
                            if (!isPlaying) onTogglePlay();
                          }}
                          sx={{ fontSize: '0.72rem', textTransform: 'none', px: 1.25, py: 0.3 }}
                        >
                          Play Clip
                        </Button>
                        {onDownloadClipPrompt && (
                          <IconButton
                            size="small"
                            onClick={() => onDownloadClipPrompt(clip)}
                            sx={{ color: '#f59e0b', bgcolor: 'rgba(245, 158, 11, 0.1)' }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Paper>
                  );
                }
              })
            )}
          </Stack>
        )}

        {/* Footer: Switch to Full Studio Mode */}
        {onToggleDesktopMode && (
          <Box sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
            <Button
              startIcon={<DesktopWindowsIcon />}
              onClick={onToggleDesktopMode}
              size="small"
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                textTransform: 'none',
                fontSize: '0.75rem',
                '&:hover': { color: '#ffffff' },
              }}
            >
              Switch to Desktop Studio Mode
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};
